import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'navy' | 'kabbi' | 'gray' | 'light' | 'dark' | 'colorful';

/**
 * Apply a theme to the document root: sets `data-theme` (which selects the
 * matching token block in index.css) and toggles the `dark` class. The default
 * `navy` theme has NO `[data-theme="navy"]` block — its tokens live on `:root`,
 * so `data-theme="navy"` simply falls through to the root defaults. Only the
 * `dark` theme uses dark surfaces; every other theme — including `navy` — is light.
 *
 * @param theme - the theme to activate
 */
export function applyTheme(theme: Theme) {
  const html = document.documentElement;
  html.setAttribute('data-theme', theme);
  if (theme === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'navy',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    // Persist key bumped to -v2 with the navy redesign so existing local
    // state (which defaulted to 'kabbi') re-defaults to the new 'navy' theme.
    { name: 'kabbi-theme-v2' },
  ),
);
